<template>
    <div class="m-0 p-0">
        <b-breadcrumb class="text-capitalize">
            <b-breadcrumb-item :to="{ name: 'AthleteList' }"><font-awesome-icon icon="caret-right" class="mr-1" />Athletes</b-breadcrumb-item>
        </b-breadcrumb>
        <div class="container-fluid px-4">

            <b-card class="mb-3" title="Athletes" sub-title="Manage your athletes. Select to view recorded sessions.">
                <b-button size="sm" variant="primary" v-on:click.stop="onAdd()">Add New Athlete</b-button>
            </b-card>

          <b-card class="my-1">
            <div class="container-fluid p-0 my-0">
                <div class="row no-gutters justify-content-start">
                    <div class="col">

                        <b-input-group size="sm" class="my-1">
                            <b-input-group-prepend>
                                <b-button variant="outline-secondary" v-on:click.stop="refreshTable()">
                                    <font-awesome-icon icon="sync-alt" class="mx-1" />
                                    Refresh
                                </b-button>
                            </b-input-group-prepend>
                            <b-input-group-prepend>
                                <b-input-group-text >
                                    <font-awesome-icon icon="search" class="mx-1" />
                                </b-input-group-text>
                            </b-input-group-prepend>
                            <b-form-input v-model="searchTerm" placeholder="Search"></b-form-input>
                        </b-input-group>
                    </div>
                </div>
            </div>
          </b-card>

            <b-overlay :show="isLoading" rounded="sm">

                <ErrorView :visible="isLoadingError" :errorValue="loadingErrorValue"/>

                <vue-good-table v-if="isLoadingError == false" styleClass="vgt-table bordered striped condensed"
                        :columns="columns"
                        :rows="items.data"
                        :sort-options="{
                          enabled: true,
                          initialSortBy: {field: 'name', type: 'asc'}
                        }"
                        :search-options="{
                            enabled: true,
                            externalQuery: searchTerm
                        }"
                        :pagination-options="{
                            enabled: true,
                            position: 'bottom',
                            perPage: 25,
                            dropdownAllowAll: false,
                            perPageDropdown: [25, 50, 100],
                        }"

                        @on-row-click="onRowClick">
                    <template slot="table-row" slot-scope="props">
                         <span v-if="props.column.field == 'name'">
                             <b-avatar class="mx-2 my-0" rounded="sm" variant="light">
                                <font-awesome-icon icon="running" />
                             </b-avatar>
                             <div style="display: inline-block; vertical-align: middle;">
                               <span>{{props.formattedRow[props.column.field].truncate(32)}}</span>
                               <br/>
                               <span style="font-size: x-small; color: #cccccc">ID:{{props.row.athleteId}}</span>
                             </div>
                        </span>
                        <span v-else-if="props.column.field == 'action'">
                            <div class="float-right">
                                <b-button size="sm" class="mx-2" variant="outline-primary" v-on:click.stop="onEdit(props.row)">
                                    <font-awesome-icon icon="edit" />
                                </b-button>
                                <b-button size="sm" class="mx-2" variant="outline-danger" v-on:click.stop="onDelete(props.row)">
                                    <font-awesome-icon icon="trash" />
                                </b-button>
                            </div>
                        </span>
                        <span v-else>
                          {{props.formattedRow[props.column.field]}}
                        </span>
                    </template>
                </vue-good-table>

            </b-overlay>
        </div>

        <AthleteModal />

        <AthleteDeleteModal />

    </div>
</template>

<script>
    import { VueGoodTable } from 'vue-good-table'
    import { mapActions, mapGetters } from 'vuex'
    import AthleteModal from './AthleteModal'
    import AthleteDeleteModal from './AthleteDeleteModal'
    import ErrorView from '../ErrorView'

    export default {
        name: "AthletesPage",
        components: {
            VueGoodTable,
            AthleteModal,
            AthleteDeleteModal,
            ErrorView
        },
        data() {
            return {

                organizationId: this.$route.params.orgId,

                searchTerm: '',
                columns: [
                    {
                        label: 'Name',
                        field: 'name',
                        sortable: true,
                    },
                    {
                        label: '',
                        field: 'summary',
                        sortable: false,
                        tdClass: 'text-capitalize'
                    },
                    {
                        label: '',
                        field: 'action',
                        sortable: false,
                    },
                ],
            }
        },
        created () {
            if(!this.organizationId) {
                this.$router.push({name: 'OrganizationList'});
            } else {
                this.refreshOrganization();
                this.refreshTable();

                this.$sessionHub.athletesOpened(this.organizationId);
                this.$sessionHub.$on('athlete-created', this.onAthleteCreated);
                this.$sessionHub.$on('athlete-changed', this.onAthleteChanged);
            }
        },
        beforeDestroy () {
            // Notify the server we stopped watching the question
            this.$sessionHub.$off('athlete-created', this.onAthleteCreated);
            this.$sessionHub.$off('athlete-changed', this.onAthleteChanged);
            this.$sessionHub.athletesClosed(this.organizationId);
        },
        computed: {
            ...mapGetters({
                items: 'athlete/items',
                itemsLoading: 'athlete/itemsLoading',
                itemsLoadingError: 'athlete/itemsLoadingError',
                itemsLoadingErrorValue: 'athlete/itemsLoadingErrorValue',
                itemsDirty: 'athlete/itemsDirty',

                athlete: 'athlete/current',
            }),

            isLoading: function() {
                return this.itemsLoading;
            },
            isLoadingError: function() {
                return this.itemsLoadingError;
            },
            loadingErrorValue: function() {
                return this.itemsLoadingErrorValue;
            }
        },
        watch: {
            itemsDirty(newValue) {
               if(newValue) {
                   this.refreshTable()
               }
            }
        },
        methods: {
            ...mapActions({
                selectOrganization: 'organization/select',
                selectAthlete: 'athlete/select',
                listAthletes: 'athlete/list',
            }),
            onRowClick(params) {
                // params.row - row object
                this.$router.push({name: 'SessionList', params: {athleteId: params.row.athleteId}})
            },

            refreshOrganization() {
                this.selectOrganization(this.organizationId)
            },
            refreshTable() {
                this.listAthletes();
            },

            onAdd() {
                this.selectAthlete(null)
                this.$bvModal.show('edit-athlete-modal')
            },
            onEdit(model) {
                this.selectAthlete(model.athleteId)
                this.$bvModal.show('edit-athlete-modal')
            },
            onDelete(model) {
                this.selectAthlete(model.athleteId)
                this.$bvModal.show('delete-athlete-modal')
            },

            onAthleteCreated({athleteData}) {
                if (this.organizationId != athleteData.organizationId)
                    return;

                this.refreshTable();
            },
            onAthleteChanged({athleteData}) {
                if (this.organizationId != athleteData.organizationId)
                    return;

                this.refreshTable();
            },
            onAthleteDeleted({athleteData}) {
                if (this.organizationId != athleteData.organizationId)
                    return;

                this.refreshTable();
            },
        }
    }
</script>

<style scoped>

</style>